// P&L — the monthly profit and loss on screen, and the overhead that feeds it.
//
// Overhead Expenses was its own sidebar entry whose only reader was the P&L
// report under Reports. The two belong together: this page shows the P&L for
// any range (same calculation Reports exports) with Overhead as its second tab.
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import OverheadExpenses from '../../components/finance/OverheadExpenses';
import QuickBooksPanel from '../../components/finance/QuickBooksPanel';
import Projections from './Projections';
import { fetchMonthlyPL, plReportRows, PL_HEADERS, PL_PRESETS, fmtMoney } from '../../lib/profitLoss';
import { downloadCSV, downloadPDF, tableHtml } from '../../lib/reportExport';
import { StatTile } from '../../components/StatTile';

// Projections sits here rather than in the sidebar (2026-09-25): it is rarely
// opened, and the one figure it produces that matters monthly — authorized
// hours × bill rate against what was delivered — is on the Overview through
// lib/authorizedRevenue.js. Retiring it is this tab plus the route.
const TABS = [
  { key: 'pl',          label: 'P&L',         desc: 'Revenue, payroll and overhead by month' },
  { key: 'overhead',    label: 'Overhead',    desc: 'Monthly costs no other table holds' },
  { key: 'projections', label: 'Projections', desc: 'What-if on authorized hours' },
  { key: 'quickbooks',  label: 'QuickBooks',  desc: 'The books, synced nightly, and what each account counts as' },
];

const money0 = (n) => '$' + Math.round(n || 0).toLocaleString('en-US');
const tone = (n) => n < 0 ? 'var(--danger)' : 'var(--text)';

function PLTable() {
  const [preset, setPreset] = useState('last_6');
  const [customStart, setCustomStart] = useState(format(subMonths(new Date(), 5), 'yyyy-MM'));
  const [customEnd,   setCustomEnd]   = useState(format(new Date(), 'yyyy-MM'));
  const [pl, setPl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const resolveRange = () => {
    if (preset === 'custom') {
      if (!customStart || !customEnd) return null;
      const start = startOfMonth(new Date(`${customStart}-01T00:00:00`));
      const end   = endOfMonth(new Date(`${customEnd}-01T00:00:00`));
      return start > end ? null : [start, end];
    }
    return PL_PRESETS.find(p => p.key === preset)?.range() || null;
  };

  useEffect(() => {
    const range = resolveRange();
    if (!range) { setError('Start month must be on or before end month.'); return; }
    let cancelled = false;
    setLoading(true); setError('');
    fetchMonthlyPL(range[0], range[1])
      .then(r => { if (!cancelled) setPl(r); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, customStart, customEnd]);

  const exportAs = (fmt) => {
    if (!pl) return;
    const title = `Monthly Profit & Loss - ${pl.rangeLabel}`;
    const rows = plReportRows(pl);
    if (fmt === 'CSV') downloadCSV(title.replace(/[^a-z0-9]/gi, '-').toLowerCase(), PL_HEADERS, rows);
    else downloadPDF(title, tableHtml(PL_HEADERS, rows));
  };

  const pill = (active) => ({ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid', background: active ? 'var(--accent)' : 'transparent', color: active ? '#fff' : 'var(--text3)', borderColor: active ? 'var(--accent)' : 'var(--border)' });
  const th = { padding: '9px 14px', fontSize: 11, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'right', whiteSpace: 'nowrap' };
  const td = { padding: '9px 14px', fontSize: 12.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {PL_PRESETS.map(p => <button key={p.key} onClick={() => setPreset(p.key)} style={pill(preset === p.key)}>{p.label}</button>)}
        {preset === 'custom' && (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', marginLeft: 6 }}>
            <input type="month" className="form-input" style={{ width: 150 }} value={customStart} onChange={e => setCustomStart(e.target.value)} />
            <span style={{ color: 'var(--text3)' }}>–</span>
            <input type="month" className="form-input" style={{ width: 150 }} value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
          </span>
        )}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
          <button className="btn btn-outline btn-sm" onClick={() => exportAs('CSV')} disabled={!pl}>Export CSV</button>
          <button className="btn btn-outline btn-sm" onClick={() => exportAs('PDF')} disabled={!pl}>Print / PDF</button>
        </span>
      </div>

      {error && <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 12 }}>{error}</div>}

      {pl && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 14 }}>
          <StatTile label="Revenue (accrual)" value={money0(pl.totals.revAccrual)} sub="by service date" tone="neutral" />
          <StatTile label="Revenue (cash)"    value={money0(pl.totals.revCash)}    sub="by paid date" tone="neutral" />
          <StatTile label="Payroll"           value={money0(pl.totals.payroll)}    sub="runs by week start" tone="neutral" />
          <StatTile label="Overhead"          value={money0(pl.totals.overhead)}   sub={pl.months.some(m => m.overheadSource === 'quickbooks') ? 'from QuickBooks' : 'Overhead tab'} tone="neutral" />
          <StatTile label="Net (accrual)"     value={money0(pl.totals.netAccrual)} sub={pl.rangeLabel} tone={pl.totals.netAccrual < 0 ? 'alert' : 'neutral'} />
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="card-title">Monthly Profit &amp; Loss</div>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>{pl?.rangeLabel || ''}</span>
        </div>
        {loading ? (
          <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
        ) : pl && (
          <div className="table-wrapper">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={{ ...th, textAlign: 'left' }}>Month</th>
                {PL_HEADERS.slice(1).map(h => <th key={h} style={th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {pl.months.map(m => (
                  <tr key={m.key} style={{ borderTop: '0.5px solid var(--border)' }}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 500 }}>{m.label}</td>
                    <td style={td}>{fmtMoney(m.revAccrual)}</td>
                    <td style={td}>{fmtMoney(m.revCash)}</td>
                    <td style={td}>{fmtMoney(m.payroll)}</td>
                    <td style={td} title={m.overheadSource === 'quickbooks' ? 'From QuickBooks' : 'From the Overhead tab'}>
                      {m.overheadSource === 'quickbooks' && <span className="badge badge-low" style={{ fontSize: 10, padding: '1px 6px', marginRight: 6 }}>QB</span>}
                      {fmtMoney(m.overhead)}
                    </td>
                    <td style={{ ...td, fontWeight: 600, color: tone(m.netAccrual) }}>{fmtMoney(m.netAccrual)}</td>
                    <td style={{ ...td, fontWeight: 600, color: tone(m.netCash) }}>{fmtMoney(m.netCash)}</td>
                  </tr>
                ))}
                {pl.months.length > 1 && (
                  <tr style={{ borderTop: '1px solid var(--border2)', background: 'var(--bg2)' }}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>Total ({pl.months.length} months)</td>
                    <td style={{ ...td, fontWeight: 600 }}>{fmtMoney(pl.totals.revAccrual)}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{fmtMoney(pl.totals.revCash)}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{fmtMoney(pl.totals.payroll)}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{fmtMoney(pl.totals.overhead)}</td>
                    <td style={{ ...td, fontWeight: 700, color: tone(pl.totals.netAccrual) }}>{fmtMoney(pl.totals.netAccrual)}</td>
                    <td style={{ ...td, fontWeight: 700, color: tone(pl.totals.netCash) }}>{fmtMoney(pl.totals.netCash)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ padding: '8px 14px', borderTop: '0.5px solid var(--border)', fontSize: 11, color: 'var(--text3)' }}>
          Revenue is home-health claim lines only — DME revenue is not yet included. Payroll includes draft runs. Overhead marked QB comes from QuickBooks (accounts set to Overhead on the QuickBooks tab); other months use the Overhead tab.
        </div>
      </div>
    </div>
  );
}

export default function ProfitLoss() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(t => t.key === params.get('tab')) ? params.get('tab') : 'pl';
  const select = (key) => setParams(prev => {
    const n = new URLSearchParams(prev);
    key === 'pl' ? n.delete('tab') : n.set('tab', key);
    return n;
  });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Finance</div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: 'var(--text)' }}>P&amp;L</h2>
        </div>
        <div role="tablist" style={{ display: 'flex', gap: 4, borderBottom: '0.5px solid var(--border)' }}>
          {TABS.map(t => (
            <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => select(t.key)} title={t.desc}
              style={{ padding: '8px 14px', fontSize: 13, fontWeight: tab === t.key ? 600 : 500, fontFamily: 'inherit', cursor: 'pointer',
                       background: 'none', border: 'none', borderBottom: `2px solid ${tab === t.key ? 'var(--accent)' : 'transparent'}`,
                       color: tab === t.key ? 'var(--text)' : 'var(--text3)', marginBottom: -1 }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {tab === 'pl' ? <PLTable /> : tab === 'overhead' ? <OverheadExpenses /> : tab === 'quickbooks' ? <QuickBooksPanel /> : <Projections />}
    </div>
  );
}
